import {
  Button,
  Group,
  NumberInput,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { client } from "@/api-client";
import type { GroupCreateInput } from "@/api-types";
import { UniversalRoutes, useNavigator } from "@/routers/routes";

interface CreateGroupFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateGroupForm = ({
  onClose,
  onSuccess,
}: CreateGroupFormProps) => {
  const createGroup = client.useMutation("post", "/trainer/groups/");
  const { navigate } = useNavigator();

  const form = useForm<GroupCreateInput>({
    initialValues: {
      name: "",
      description: "",
      maxParticipants: 8,
    },
    validate: {
      name: (value) =>
        value.length < 2 ? "Name must be at least 2 characters" : null,
      maxParticipants: (value) =>
        value < 1 ? "Must have at least 1 participant" : null,
    },
  });

  const handleSubmit = async (values: GroupCreateInput) => {
    const result = await createGroup.mutateAsync({ body: values });
    onSuccess?.();
    onClose();
    navigate({
      path: UniversalRoutes.GROUPS.DETAIL,
      params: { groupId: result.groupId },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label="Group Name"
          placeholder="Enter group name"
          required
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          placeholder="Describe what this group is about (optional)"
          rows={3}
          {...form.getInputProps("description")}
        />

        <NumberInput
          label="Maximum Participants"
          description="This is a guideline - you can approve more participants if needed"
          min={1}
          max={50}
          required
          {...form.getInputProps("maxParticipants")}
        />

        <Group justify="flex-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={createGroup.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={createGroup.isPending}>
            Create Group
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
